import { useEffect, useRef, useState } from 'react';
import { useTranslationContext } from '../../../../../../../context/TranslationContext';

interface Props {
  onPlaceSelected: (location: google.maps.LatLngLiteral, address?: string) => void;
}

const LocationSearch = ({ onPlaceSelected }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const t = useTranslationContext();

  useEffect(() => {
    if (!inputRef.current) return;

    const initializeAutocomplete = () => {
      if (!window.google?.maps?.places) {
        console.warn('Google Places API no está disponible aún');
        return false;
      }

      // Inicializar servicios necesarios
      autocompleteRef.current = new window.google.maps.places.AutocompleteService();

      // Crear un elemento temporal para PlacesService (requerido por la API)
      const dummyMap = document.createElement('div');
      // @ts-ignore - Google Maps types inconsistency
      placesServiceRef.current = new window.google.maps.places.PlacesService(dummyMap);

      // Crear un session token para agrupar las peticiones
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

      return true;
    };

    // Intentar inicializar inmediatamente si la API ya está cargada
    if (!initializeAutocomplete()) {
      // Si no está lista, intentar después de un breve delay
      const timeout = setTimeout(() => {
        initializeAutocomplete();
      }, 500);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, []);

  // Manejar cambios en el input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value || value.length < 3 || !autocompleteRef.current) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    // Buscar predicciones
    autocompleteRef.current.getPlacePredictions(
      {
        input: value,
        types: ['geocode'],
        sessionToken: sessionTokenRef.current || undefined,
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setPredictions(predictions);
          setShowPredictions(true);
        } else {
          setPredictions([]);
          setShowPredictions(false);
        }
      },
    );
  };

  // Manejar selección de una predicción
  const handlePredictionClick = (placeId: string, description: string) => {
    if (!placesServiceRef.current) return;

    // Obtener detalles del lugar seleccionado
    placesServiceRef.current.getDetails(
      {
        placeId: placeId,
        fields: ['geometry', 'formatted_address'],
        sessionToken: sessionTokenRef.current || undefined,
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place?.geometry?.location
        ) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const formattedAddress = place.formatted_address ?? description;

          onPlaceSelected({ lat, lng }, formattedAddress);

          // Actualizar el input con la dirección seleccionada
          if (inputRef.current) {
            inputRef.current.value = formattedAddress;
          }

          // Ocultar predicciones y crear nuevo session token
          setShowPredictions(false);
          setPredictions([]);
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
      },
    );
  };

  // Cerrar predicciones al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowPredictions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        maxWidth: 300,
        margin: '0 auto',
        zIndex: 13000,
        top: '10px',
        left: '10px',
      }}
    >
      <input
        ref={inputRef}
        placeholder={t('global.searchLocation', 'Buscar en Google Maps')}
        onChange={handleInputChange}
        onFocus={() => predictions.length > 0 && setShowPredictions(true)}
        style={{
          width: '100%',
          padding: '12px 16px 5px',
          borderRadius: '24px',
          border: '1px solid #ccc',
          backgroundColor: 'white',
          fontSize: '15px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          outline: 'none',
          position: 'relative',
          zIndex: 13000,
        }}
      />
      {showPredictions && predictions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            marginTop: '4px',
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 13001,
          }}
        >
          {predictions.map((prediction) => (
            <div
              key={prediction.place_id}
              onClick={() => handlePredictionClick(prediction.place_id, prediction.description)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '14px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <div style={{ fontWeight: 500 }}>{prediction.structured_formatting.main_text}</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {prediction.structured_formatting.secondary_text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
